import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import {
  MeetingInsightsDto,
  MeetingOwnerKind,
  MeetingTopicDto,
} from '@interfaces/meetings/responses/meeting-insights.response';
import { SharedModule } from '@src/app/shared/shared.module';
import { MeetingEvidenceComponent } from '@features/dashboard/meetings/pages/meeting-detail/components/meeting-evidence/meeting-evidence.component';
import {
  estadoDoResumo,
  rotuloDoTopico,
} from '@features/dashboard/meetings/pages/meeting-detail/meeting-insights-state';

const LADOS: Record<MeetingOwnerKind, string> = {
  Legatus: 'Legatus',
  Company: 'Empresa',
  Partner: 'Parceiro',
  Bank: 'Banco',
  Unknown: '',
};

/**
 * Aba **Resumo** do detalhe: o que o agente de resumo do Cog tira da reunião (PBI 3225, task 3228),
 * no estilo do Fireflies e adaptado à Legatus: resumo, decisões, próximos passos e pendências (com
 * responsável e prazo quando ditos) e os tópicos de negócio, cada item com o trecho da fala que o
 * sustenta.
 *
 * Estados: sem transcrição, ainda não gerado ("Gerar resumo"), processando, falhou ("Gerar de novo")
 * e pronto. A tela não mostra custo, tokens nem modelo de IA, e gerar é um clique, sem confirmação.
 */
@Component({
  selector: 'app-meeting-summary-panel',
  standalone: true,
  imports: [SharedModule, MeetingEvidenceComponent],
  templateUrl: './meeting-summary-panel.component.html',
  styleUrl: './meeting-summary-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeetingSummaryPanelComponent {
  readonly insights = input<MeetingInsightsDto | null>(null);
  /** Sem transcrição o Cog não tem o que resumir. */
  readonly temTranscricao = input(false);
  /** A gravação ainda processa: a transcrição (e o resumo) estão a caminho. */
  readonly transcricaoACaminho = input(false);
  readonly carregando = input(false);
  readonly erro = input<string | null>(null);
  /** Libera "Gerar resumo" e "Gerar de novo" (time interno pela visão geral). */
  readonly podeGerar = input(false);
  readonly gerando = input(false);
  readonly temVideo = input(false);
  /** Time interno: mostra o link discreto "Sessões do Cog" (custo e sessão de cada agente). */
  readonly podeVerSessoes = input(false);

  readonly gerar = output<void>();
  readonly verSessoes = output<void>();
  readonly irPara = output<number>();

  protected readonly estado = computed(() =>
    estadoDoResumo(this.insights(), this.temTranscricao(), this.carregando(), this.erro())
  );

  /** Pronto, mas sem nada a mostrar (call curta, só cumprimentos). */
  protected readonly vazio = computed(() => {
    const i = this.insights();
    return (
      !!i &&
      !i.summary &&
      !i.decisions.length &&
      !i.nextSteps.length &&
      !i.pendingItems.length &&
      !i.topics.length
    );
  });

  /** Há execução de agente para inspecionar (o backend só manda `agentRuns` para o time interno). */
  protected readonly temSessoes = computed(
    () => this.podeVerSessoes() && !!this.insights()?.agentRuns?.length
  );

  /** Pode pedir outra rodada (time interno e o backend libera). */
  protected readonly podeGerarAgora = computed(
    () => this.podeGerar() && !!this.insights()?.canReprocess
  );

  protected rotuloDoTopico(topico: MeetingTopicDto): string {
    return rotuloDoTopico(topico);
  }

  /** Segundo do primeiro trecho do tópico, para o chip levar o vídeo até lá. */
  protected inicioDoTopico(topico: MeetingTopicDto): number | null {
    return topico.evidence.find(e => e.startSeconds !== null)?.startSeconds ?? null;
  }

  protected lado(kind: MeetingOwnerKind | null): string {
    return kind ? LADOS[kind] : '';
  }
}
